import type { FC } from "react";

import type { ChannelEnvironment } from "../useChannelEnvironment";
import type {
  ChannelWorkspaceRepository,
  ChannelWorkspaceRepositoryRefreshResponse,
  WorkspaceRepositoryPermission,
} from "../useChannelWorkspaceRepositories";

import { useOptionalToast } from "../../../context/ToastContext";
import { DependenciesBlock } from "./Dependencies";
import { getRepositoryDetachState, shortSha } from "./environmentUtils";
import { pushPolicyKey, usePushPolicies } from "./usePushPolicies";

/**
 * A `detached` row is a terminal tombstone, not an attachment: it has no
 * checkout, offers no actions, and must not be counted as attached. Splitting
 * it into a read-only history section is what keeps the record member-visible
 * without letting it behave like a live repository.
 */
export function selectActiveRepositories(
  repositories: readonly ChannelWorkspaceRepository[],
): ChannelWorkspaceRepository[] {
  return repositories.filter((repository) => repository.status !== "detached");
}

export function selectDetachedRepositories(
  repositories: readonly ChannelWorkspaceRepository[],
): ChannelWorkspaceRepository[] {
  return repositories.filter((repository) => repository.status === "detached");
}

/** Zero-repository state. The rail gets one muted line: the attach affordance
 *  sits directly above it, so a dashed callout box restating "none" would spend
 *  ~60px of a 240px column on a fact already visible. Full width keeps the
 *  callout and the teaching copy, where the space is free. */
const RepositoriesEmptyState: FC<{ compact?: boolean }> = ({
  compact = false,
}) => {
  if (compact) {
    return (
      <p className="font-mono text-[10px] text-slate-500 dark:text-slate-500">
        none attached
      </p>
    );
  }
  return (
    <div className="border border-dashed border-slate-300/70 bg-slate-50/50 px-3 py-4 text-center dark:border-slate-700/70 dark:bg-slate-900/40">
      <div className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">
        No repositories attached
      </div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.1em] text-slate-500">
        Attach one to give actors code to work in.
      </div>
    </div>
  );
};

export function RepositoriesBlock({
  compact = false,
  environment,
  onAcknowledgeAbsentDetach,
  onAttach,
  onChangeBranch,
  onDetach,
  onRefresh,
  onRestart,
  repositories,
}: {
  /**
   * Presentation variant for the 240px inspector rail. It only tightens
   * spacing and truncation — never a second lifecycle renderer, and never a
   * reason to hide state behind hover.
   */
  compact?: boolean;
  environment: ChannelEnvironment;
  onAcknowledgeAbsentDetach?: (repository: ChannelWorkspaceRepository) => void;
  onAttach: () => void;
  onChangeBranch?: (repository: ChannelWorkspaceRepository) => void;
  onDetach: (repository: ChannelWorkspaceRepository) => void;
  onRefresh: (repository: ChannelWorkspaceRepository) => void;
  onRestart?: () => void;
  repositories: ChannelWorkspaceRepository[];
}) {
  const activeRepositories = selectActiveRepositories(repositories);
  const detachedRepositories = selectDetachedRepositories(repositories);
  // Push policy state is per (environment, provider, repository) — exactly the
  // attachment key — so one fetch covers every row. The badge flips the
  // orchestrator policy row; fence/mint do the actual enforcement.
  const pushPolicies = usePushPolicies(environment.channelEnvironmentId);
  const toast = useOptionalToast();
  const handleTogglePush = async (
    repository: ChannelWorkspaceRepository,
    pushEnabled: boolean,
  ) => {
    try {
      await pushPolicies.setPolicy(
        repository.providerId,
        repository.repositoryId,
        pushEnabled,
      );
    } catch (error) {
      toast?.present({
        body: error instanceof Error ? error.message : String(error),
        title: pushEnabled
          ? "Failed to enable repository push"
          : "Failed to disable repository push",
        tone: "error",
      });
    }
  };
  const pendingRepositories = activeRepositories.filter(
    (repository) =>
      repository.status === "pending" ||
      repository.status === "detach_pending" ||
      repository.pendingBranch != null,
  );
  const detachStates = pendingRepositories.map(getRepositoryDetachState);
  const acknowledgementCount = detachStates.filter(
    (state) => state === "acknowledgement",
  ).length;
  const blockedCount = detachStates.filter(
    (state) => state === "blocked",
  ).length;
  const waitingCount = detachStates.filter(
    (state) => state === "waiting",
  ).length;
  const retryCount = detachStates.filter((state) => state === "retry").length;
  const restartRequiredCount = pendingRepositories.filter(
    (repository) => repository.pendingReason === "egress_requires_restart",
  ).length;
  const queuedCount = pendingRepositories.filter(
    (repository) =>
      repository.pendingReason !== "egress_requires_restart" &&
      (getRepositoryDetachState(repository) === null ||
        getRepositoryDetachState(repository) === "queued"),
  ).length;
  const pendingSummary = [
    acknowledgementCount > 0
      ? `${acknowledgementCount} repository detach${acknowledgementCount === 1 ? " requires" : "es require"} acknowledgement.`
      : null,
    blockedCount > 0
      ? `${blockedCount} repository detach${blockedCount === 1 ? " is" : "es are"} blocked by checkout safety checks.`
      : null,
    waitingCount > 0
      ? `${waitingCount} repository detach${waitingCount === 1 ? " is" : "es are"} waiting for processes or the workspace runtime.`
      : null,
    retryCount > 0
      ? `${retryCount} repository detach${retryCount === 1 ? " is" : "es are"} awaiting retry${environment.status === "running" ? "." : " once the workspace starts or resumes."}`
      : null,
    restartRequiredCount > 0
      ? `${restartRequiredCount} repository change${restartRequiredCount === 1 ? " requires" : "s require"} a workspace restart.`
      : null,
    queuedCount > 0
      ? `${queuedCount} repository change${queuedCount === 1 ? " is" : "s are"} queued${environment.status === "running" ? " for background processing." : " until the workspace starts or resumes."}`
      : null,
  ].filter((message) => message !== null);
  return (
    <div className="grid min-w-0 grid-cols-1 gap-2">
      {/* In the rail the heading is a RailStat-style label, not a panel: the
          bordered emerald bar plus a count chip spends ~40px of a 240px column
          restating what the row below already says. Full width keeps the panel,
          where it reads as a section boundary rather than noise. */}
      <div
        className={
          compact
            ? "flex flex-wrap items-center justify-between gap-2"
            : "flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border border-emerald-400/30 bg-emerald-400/[0.05] px-3 py-2 dark:border-emerald-500/20 dark:bg-emerald-500/[0.04]"
        }
      >
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={
              compact
                ? "font-mono text-[8px] font-bold uppercase tracking-[0.25em] text-slate-400 dark:text-slate-600"
                : "font-mono text-[10px] font-black uppercase tracking-[0.24em] text-slate-700 dark:text-slate-200"
            }
          >
            Repositories
          </span>
          {/* The count stays in the rail -- it is information (how many
              attachments are live, tombstones excluded), not chrome. Only the
              bordered chip is dropped, so it costs no height. */}
          <span
            className={
              compact
                ? "font-mono text-[9px] font-bold tabular-nums text-slate-500"
                : "border border-slate-400/40 bg-slate-400/10 px-1.5 py-0.5 font-mono text-[9px] font-bold text-slate-500"
            }
          >
            {activeRepositories.length}
          </span>
        </div>
        <button
          type="button"
          onClick={onAttach}
          className={`shrink-0 whitespace-nowrap font-mono font-bold uppercase tracking-[0.18em] text-emerald-700 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-100 ${
            compact ? "text-[9px]" : "text-[10px]"
          }`}
        >
          [ + attach ]
        </button>
      </div>

      {pushPolicies.error && (
        <div
          className={`font-mono text-amber-700 dark:text-amber-300 ${compact ? "text-[9px]" : "text-[10px]"}`}
        >
          push policy state unavailable: {pushPolicies.error} — badges show the
          default-off state; the orchestrator enforces regardless.
        </div>
      )}
      {activeRepositories.length === 0 ? (
        <RepositoriesEmptyState compact={compact} />
      ) : (
        <div className="grid min-w-0 grid-cols-1 gap-1.5">
          {activeRepositories.map((repository) => (
            <RepoRow
              key={repository.id}
              compact={compact}
              repository={repository}
              runtimeRunning={environment.status === "running"}
              pushEnabled={
                pushPolicies.policies.get(
                  pushPolicyKey(repository.providerId, repository.repositoryId),
                ) ?? false
              }
              onTogglePush={(pushEnabled) =>
                void handleTogglePush(repository, pushEnabled)
              }
              onAcknowledgeAbsentDetach={
                onAcknowledgeAbsentDetach
                  ? () => onAcknowledgeAbsentDetach(repository)
                  : undefined
              }
              onChangeBranch={
                onChangeBranch ? () => onChangeBranch(repository) : undefined
              }
              onDetach={() => onDetach(repository)}
              onRefresh={() => onRefresh(repository)}
            />
          ))}
        </div>
      )}

      <DependenciesBlock environmentId={environment.channelEnvironmentId} />

      {detachedRepositories.length > 0 && (
        <div className="grid min-w-0 grid-cols-1 gap-1 border border-slate-300/60 bg-slate-50/50 px-3 py-2 dark:border-slate-700/60 dark:bg-slate-900/40">
          <span className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">
            Detached history ({detachedRepositories.length})
          </span>
          {detachedRepositories.map((repository) => (
            <DetachedRepoRow
              key={repository.id}
              compact={compact}
              repository={repository}
            />
          ))}
        </div>
      )}

      {pendingSummary.length > 0 && (
        <div
          className={`flex flex-wrap items-center justify-between gap-2 font-mono text-amber-700 dark:text-amber-300 ${
            compact
              ? // No box in the rail: a bordered amber panel around an amber
                // sentence that already sits under an amber row reason is two
                // frames for one fact. The left rule matches the row's reason
                // marker, and dropping uppercase + 0.14em tracking is what takes
                // this from three wrapped lines to one.
                "border-l-2 border-amber-500/40 pl-2 text-[10px] leading-snug"
              : "border border-amber-500/30 bg-amber-500/5 px-3 py-1.5 text-[9px] uppercase tracking-[0.14em]"
          }`}
        >
          <div className="grid gap-1">
            {pendingSummary.map((message) => (
              <p key={message}>{message}</p>
            ))}
          </div>
          {onRestart &&
            restartRequiredCount > 0 &&
            environment.status === "running" && (
              <button
                type="button"
                onClick={onRestart}
                className={`font-bold uppercase text-amber-800 hover:text-amber-950 dark:text-amber-200 dark:hover:text-amber-50 ${
                  compact ? "tracking-[0.1em]" : "tracking-[0.16em]"
                }`}
              >
                [ restart workspace ]
              </button>
            )}
        </div>
      )}
    </div>
  );
}

function DetachedRepoRow({
  compact,
  repository,
}: {
  compact: boolean;
  repository: ChannelWorkspaceRepository;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[10px] text-slate-500">
      <span
        className={`min-w-0 truncate text-slate-600 dark:text-slate-400 ${compact ? "basis-full" : ""}`}
      >
        {repository.remoteSlug}
      </span>
      <PermissionBadge compact={compact} permission={repository.permission} />
      <RepoStatusBadge compact={compact} status={repository.status} />
      <span title={repository.updatedAt}>
        {repository.updatedAt.slice(0, 10)}
      </span>
      {repository.lastError && (
        <span className="w-full break-words text-slate-500">
          {repository.lastError}
        </span>
      )}
    </div>
  );
}

function RepoRow({
  compact,
  onAcknowledgeAbsentDetach,
  onChangeBranch,
  onDetach,
  onRefresh,
  onTogglePush,
  pushEnabled,
  repository,
  runtimeRunning,
}: {
  compact: boolean;
  onAcknowledgeAbsentDetach?: () => void;
  onChangeBranch?: () => void;
  onDetach: () => void;
  onRefresh: () => void;
  onTogglePush: (pushEnabled: boolean) => void;
  pushEnabled: boolean;
  repository: ChannelWorkspaceRepository;
  runtimeRunning: boolean;
}) {
  const branch = repository.defaultBranch ?? repository.repositoryDefaultBranch;
  const pendingBranch = repository.pendingBranch ?? null;
  const detaching = repository.status === "detach_pending";
  const detachState = getRepositoryDetachState(repository);
  const detachMeta =
    detachState === null ? null : DETACH_STATE_META[detachState];
  const canRefresh =
    repository.status === "ready" && pendingBranch == null && !detaching;
  const canChangeBranch =
    onChangeBranch !== undefined &&
    repository.status !== "detach_pending" &&
    repository.status !== "detached";
  const lastError = repository.lastError
    ? cleanRepositoryLastError(repository.lastError)
    : null;
  const restartRequired =
    repository.pendingReason === "egress_requires_restart";
  // Driven by the typed reason rather than the stored message so the two retry
  // classes stay visibly different: one is the provider refusing a credential
  // we hold, the other is us failing to produce a credential at all.
  const retryPending = cloneRetryMessage(repository.pendingReason);
  return (
    <div
      className={`min-w-0 border border-slate-300/70 bg-slate-50/70 dark:border-slate-700/70 dark:bg-slate-900/50 ${compact ? "px-2 py-1.5" : "px-3 py-2"}`}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {/* In the rail the name takes the full first line: sharing it with the
            permission and status badges truncated "artifax-tech/agent-arti" to
            "Artifax\u2026", i.e. the row's identity lost to two 9px chips. Badges
            wrap to the next line, which costs ~14px and reads far better. */}
        <span
          title={repository.remoteSlug}
          className={`min-w-0 truncate font-mono font-bold text-slate-900 dark:text-slate-100 ${
            compact ? "basis-full text-[11px]" : "flex-1 text-[12px]"
          }`}
        >
          {repository.remoteSlug}
        </span>
        <PermissionBadge compact={compact} permission={repository.permission} />
        {repository.permission === "write" &&
          (repository.status === "ready" ||
            repository.status === "refreshing") && (
            <PushPolicyBadge
              compact={compact}
              pushEnabled={pushEnabled}
              onToggle={() => onTogglePush(!pushEnabled)}
            />
          )}
        {pendingBranch != null && (
          <span
            title="Branch change pending — applies in the background, or when the workspace next starts or resumes"
            className={`animate-pulse border border-amber-400/50 bg-amber-400/10 font-mono font-black uppercase text-amber-700 dark:text-amber-300 ${badgeSizeClass(compact)}`}
          >
            branch
          </span>
        )}
        <RepoStatusBadge
          compact={compact}
          label={detachMeta?.label}
          status={repository.status}
        />
      </div>
      <div
        className={`mt-1 gap-x-2 gap-y-1 font-mono text-[10px] text-slate-500 ${compact ? "grid min-w-0 grid-cols-1" : "flex flex-wrap items-center"}`}
      >
        <span className="flex min-w-0 items-center gap-2">
          {canChangeBranch ? (
            <button
              type="button"
              title="Change branch"
              onClick={onChangeBranch}
              className="min-w-0 cursor-pointer truncate whitespace-nowrap hover:text-sky-600 dark:hover:text-sky-400"
            >
              ⎇ {branch ?? "default"}
              {pendingBranch != null && (
                <span className="text-amber-600 dark:text-amber-400">
                  {" "}
                  → {pendingBranch}
                </span>
              )}
            </button>
          ) : (
            <span
              title="default branch"
              className="min-w-0 truncate whitespace-nowrap"
            >
              ⎇ {branch ?? "default"}
            </span>
          )}
          {(repository.status === "ready" ||
            repository.status === "refreshing") &&
            repository.lastCommitSha && (
              <span
                title={repository.lastCommitSha}
                className={`shrink-0 whitespace-nowrap tabular-nums ${
                  repository.status === "refreshing"
                    ? "text-sky-600 dark:text-sky-400"
                    : "text-slate-400 dark:text-slate-500"
                }`}
              >
                {shortSha(repository.lastCommitSha)}
              </span>
            )}
        </span>
        <span className="ml-auto flex min-w-0 flex-wrap items-center gap-2 text-[9px]">
          {canRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              title="Fast-forward checkout to the latest branch head"
              className="whitespace-nowrap uppercase tracking-[0.1em] text-slate-400 hover:text-sky-600 dark:text-slate-500 dark:hover:text-sky-400"
            >
              ⟳ refresh
            </button>
          )}
          {!detaching && (
            <button
              type="button"
              onClick={onDetach}
              /* Detach stays visible (never hover-revealed) but is muted in the
               rail: permanent rose made the destructive action the brightest
               thing in a 240px panel, louder than the repository it would
               destroy. It returns to rose on hover, when intent is present. */
              className={`whitespace-nowrap uppercase tracking-[0.1em] ${
                compact
                  ? "text-slate-400 hover:text-rose-600 dark:text-slate-500 dark:hover:text-rose-400"
                  : "text-rose-600 hover:text-rose-800 dark:text-rose-400 dark:hover:text-rose-200"
              }`}
            >
              detach
            </button>
          )}
        </span>
      </div>
      {(repository.status === "errored" ||
        repository.status === "pending" ||
        detaching) &&
        (lastError || restartRequired || retryPending || detachMeta) && (
          <div
            className={`mt-1.5 break-words border-l-2 pl-2 ${
              compact ? "text-[10px] leading-snug" : "text-xs leading-5"
            } ${
              repository.status === "errored"
                ? "border-rose-500/40 text-rose-600 dark:text-rose-300"
                : "border-amber-500/40 text-amber-700 dark:text-amber-300"
            }`}
          >
            {restartRequired
              ? // The aggregate banner below owns the instruction and the
                // restart button. Repeating "restart the workspace to apply"
                // here put the same sentence in two stacked amber blocks, so
                // this line states the cause instead: what changed, on which
                // repository, and that it is not live yet.
                "Network access changed \u2014 not applied yet."
              : (retryPending ?? lastError)}
            {detachMeta && (
              <span className="mt-0.5 block text-slate-500">
                {detachMeta.guidance}
                {detachState === "retry" &&
                  (runtimeRunning
                    ? " Arti retries about once a minute while the workspace is running."
                    : " Retries resume once the workspace starts or resumes.")}
                {(detachState === "queued" || detachState === "waiting") &&
                  !runtimeRunning &&
                  " The workspace must start or resume before detach can proceed."}
              </span>
            )}
          </div>
        )}
      {/* Server-derived: the action exists only when the durable blocker is
          exactly an absent checkout, so the client cannot manufacture it. */}
      {onAcknowledgeAbsentDetach &&
        repository.detachResolution === "acknowledge_absent_checkout" && (
          <button
            type="button"
            onClick={onAcknowledgeAbsentDetach}
            className="mt-1.5 self-start font-mono text-[10px] uppercase tracking-[0.16em] text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
          >
            [ acknowledge absent checkout ]
          </button>
        )}
      {repository.branchChangeError != null && (
        <div className="mt-1.5 break-words border-l-2 border-amber-500/40 pl-2 text-xs leading-5 text-amber-700 dark:text-amber-300">
          {repository.branchChangeError}
        </div>
      )}
    </div>
  );
}

/**
 * Mirrors the persistence-side detach blocker prefix. Duplicated rather than
 * imported because `@arti/persistence` must not reach the browser bundle; the
 * typed `detachBlocker` field is the machine-readable contract, this only
 * removes the marker from the human-readable text.
 */
/**
 * Driven by the typed reason rather than the stored message so the two retry
 * classes stay visibly different: one is the provider refusing a credential we
 * hold, the other is us failing to produce a credential at all.
 */
function cloneRetryMessage(
  pendingReason: ChannelWorkspaceRepository["pendingReason"],
): string | null {
  if (pendingReason === "provider_retry") {
    return "The source-control provider denied access to this configured repository. Arti is retrying.";
  }
  if (pendingReason === "credential_retry") {
    return "The workspace credential service could not supply a clone credential. Arti is retrying.";
  }
  return null;
}

const DETACH_BLOCKER_PREFIX_PATTERN = /^\[repository_detach:[a-z_]+\]\s*/u;

export function cleanRepositoryLastError(lastError: string): string {
  if (DETACH_BLOCKER_PREFIX_PATTERN.test(lastError)) {
    return lastError.replace(DETACH_BLOCKER_PREFIX_PATTERN, "");
  }
  if (/command failed|docker\s+exec|\/workspace/iu.test(lastError)) {
    return "Repository clone failed. Check the branch and source-control credential, then retry.";
  }
  return lastError;
}

export function formatRefreshResult(
  result: NonNullable<
    ChannelWorkspaceRepositoryRefreshResponse["refreshResult"]
  >,
): string {
  const commits = `${shortSha(result.oldCommitSha)}→${shortSha(result.newCommitSha)}`;
  if (!result.hasConflicts) {
    return `Repository ${result.force ? "force-" : ""}refreshed ${commits}; ${result.cleanFiles.length} files updated cleanly.`;
  }
  const stash = result.autostashRetained
    ? ` Stash ${result.autostashShortId ?? "created"} retained.`
    : "";
  return `Repository force-refreshed ${commits}; ${result.conflictedFiles.length} files conflicted (${formatShortFileList(result.conflictedFiles)}) and ${result.cleanFiles.length} files updated cleanly. Resolve conflicts before building.${stash}`;
}

function formatShortFileList(files: string[]): string {
  const limit = 4;
  const visible = files.slice(0, limit).join(", ");
  return files.length > limit
    ? `${visible}, +${files.length - limit} more`
    : visible;
}

/** Badge geometry. The rail's dialect is 8px labels over 10px values, so the
 *  full-width chip size (9px black on 1.5/0.5 padding) reads as shouting in a
 *  240px column: two of them plus a 12px name made one repository row taller
 *  than STATUS, SANDBOX and COMPUTE combined. Colour coding is the point of
 *  these chips, so compact keeps the border and tone and only sheds bulk. */
const badgeSizeClass = (compact: boolean): string =>
  compact
    ? "px-1 py-0 text-[8px] tracking-[0.12em]"
    : "px-1.5 py-0.5 text-[9px] tracking-[0.16em]";

/**
 * Per-attachment push policy toggle. Shown only for write+ready attachments:
 * a write permission alone grants nothing (the fence also requires this
 * policy row + allowlist + push-plane installation), and a non-ready checkout has
 * no meaningful HEAD to publish. The badge is an operator affordance — the
 * actual enforcement lives in the orchestrator fence/mint gates.
 */
function PushPolicyBadge({
  compact = false,
  onToggle,
  pushEnabled,
}: {
  compact?: boolean;
  onToggle: () => void;
  pushEnabled: boolean;
}) {
  return (
    <button
      type="button"
      title={
        pushEnabled
          ? "Attachment push on — the broker may mint a single-repo write token for this attachment. Click to disable (repository writes stay authorized org-wide)."
          : "Attachment push off — the broker refuses to fence or mint for this attachment. Click to enable; the repository must be authorized for writes org-wide (Source Control settings)."
      }
      onClick={onToggle}
      className={`cursor-pointer border font-mono font-black uppercase ${badgeSizeClass(compact)} ${
        pushEnabled
          ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-700 hover:bg-emerald-400/20 dark:text-emerald-300"
          : "border-slate-400/40 bg-slate-400/10 text-slate-500 hover:bg-slate-400/20 dark:text-slate-400"
      }`}
    >
      {pushEnabled ? "push on" : "push off"}
    </button>
  );
}

function PermissionBadge({
  compact = false,
  permission,
}: {
  compact?: boolean;
  permission: WorkspaceRepositoryPermission;
}) {
  const write = permission === "write";
  return (
    <span
      title={
        write
          ? "WRITE unlocks pushChanges through the host push broker — credentials stay read-only; actors never push"
          : "Clone and pull only"
      }
      className={`border font-mono font-black uppercase ${badgeSizeClass(compact)} ${
        write
          ? "border-amber-400/50 bg-amber-400/10 text-amber-700 dark:text-amber-300"
          : "border-slate-400/40 bg-slate-400/10 text-slate-500 dark:text-slate-400"
      }`}
    >
      {write ? "write" : "read"}
    </span>
  );
}

export const REPO_STATUS_META: Record<
  ChannelWorkspaceRepository["status"],
  { label: string; pulse?: boolean; tone: keyof typeof REPO_BADGE_TONE_CLASS }
> = {
  cloning: { label: "cloning", pulse: true, tone: "info" },
  detach_pending: { label: "detach queued", tone: "warning" },
  detached: { label: "detached", tone: "neutral" },
  errored: { label: "error", tone: "danger" },
  pending: { label: "pending", tone: "neutral" },
  ready: { label: "ready", tone: "success" },
  refreshing: { label: "refreshing", pulse: true, tone: "info" },
};

const DETACH_STATE_META: Record<
  NonNullable<ReturnType<typeof getRepositoryDetachState>>,
  { guidance: string; label: string }
> = {
  acknowledgement: {
    guidance:
      "Operator acknowledgement is required to complete this detach while the checkout remains absent. Automatic retries cannot resolve an absent checkout.",
    label: "needs acknowledgement",
  },
  blocked: {
    guidance:
      "Resolve the checkout safety issue before detach can complete. Automatic checks do not discard local work.",
    label: "detach blocked",
  },
  queued: {
    guidance:
      "Waiting for the owner runtime to verify checkout safety. Detach completion has not been confirmed.",
    label: "detach queued",
  },
  retry: {
    guidance:
      "Detach verification or removal failed; another attempt is pending.",
    label: "detach retry pending",
  },
  waiting: {
    guidance:
      "Detach is waiting for running processes to finish or for the workspace runtime to become available.",
    label: "detach waiting",
  },
};

const REPO_BADGE_TONE_CLASS = {
  danger: "border-rose-400/50 bg-rose-400/10 text-rose-700 dark:text-rose-300",
  info: "border-sky-400/50 bg-sky-400/10 text-sky-700 dark:text-sky-300",
  neutral:
    "border-slate-400/40 bg-slate-400/10 text-slate-500 dark:text-slate-400",
  success:
    "border-emerald-400/50 bg-emerald-400/10 text-emerald-700 dark:text-emerald-300",
  warning:
    "border-amber-400/50 bg-amber-400/10 text-amber-700 dark:text-amber-300",
} as const;

function RepoStatusBadge({
  compact = false,
  label,
  status,
}: {
  compact?: boolean;
  label?: string;
  status: ChannelWorkspaceRepository["status"];
}) {
  const meta = REPO_STATUS_META[status];
  return (
    <span
      className={`border font-mono font-black uppercase ${badgeSizeClass(compact)} ${REPO_BADGE_TONE_CLASS[meta.tone]} ${meta.pulse ? "animate-pulse" : ""}`}
    >
      {label ?? meta.label}
    </span>
  );
}
